"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.createStatHandler = exports.updateUserForHandler = exports.flushBatch = exports.flushBatches = exports.clearBatches = exports.trackBatch = exports.track = exports.page = exports.identify = void 0;
const StatTypes_1 = require("./StatTypes");
const stats_api_1 = __importDefault(require("./stats-api"));
const StatsApi = stats_api_1.default;
let NEXT_STAT_HANDLER_ID = 1;
let FULLY_ANONYMOUS_ID;
function getFullyAnonymousId() {
    if (!FULLY_ANONYMOUS_ID) {
        FULLY_ANONYMOUS_ID = createAnonymousId();
    }
    return FULLY_ANONYMOUS_ID;
}
function createAnonymousId() {
    return StatsApi.UUID();
}
function identify(statHandler, apiKey, user) {
    if (!user.id) {
        statHandler.app.sendError('Stats invalid identify', 'Empty user id');
        return;
    }
    let anonymousId = statHandler.anonymousId;
    let userId = statHandler.userId;
    if (userId !== user.id) {
        if (userId) {
            anonymousId = createAnonymousId();
        }
        userId = user.id;
        if ((anonymousId !== statHandler.anonymousId && statHandler.anonymousId) ||
            (userId !== statHandler.userId && statHandler.userId)) {
            track(statHandler, apiKey, 'user_identifiers_changed', {
                previous_anonymous_id: statHandler.anonymousId,
                previous_user_id: statHandler.userId,
                new_anonymous_id: anonymousId,
                new_user_id: userId,
            });
        }
        statHandler.app.storeUserIds({
            userId: userId,
            anonymousId: anonymousId,
        });
        updateUserForHandler(statHandler, anonymousId, userId, 'identify');
    }
    const params = {
        apiKey: apiKey,
        apiHost: statHandler.apiHost,
        context: getStatContext(statHandler),
        timestamp: new Date(),
        anonymousId: anonymousId,
        userId: userId,
        user: {
            id: user.id,
            name: user.name,
            email: user.email,
        },
    };
    sendEvent(statHandler, StatTypes_1.StatFlags.None, {
        event: 'identify',
        payload: params,
    });
}
exports.identify = identify;
function buildTrackParams(statHandler, apiKey, event, inputProperties, flags, timestamp) {
    let result = undefined;
    const onlyFirstSession = flags & StatTypes_1.StatFlags.OnlyFirstSession;
    if (!onlyFirstSession || statHandler.isFirstSession) {
        let canBeSend = true;
        const onceInSession = flags & StatTypes_1.StatFlags.OnceInSession;
        if (onceInSession) {
            canBeSend = !statHandler.onceInSessionSentEvents.has(event);
            if (canBeSend) {
                statHandler.onceInSessionSentEvents.add(event);
            }
        }
        if (canBeSend) {
            let properties = inputProperties || {};
            if (!(flags & StatTypes_1.StatFlags.NoCommonParams)) {
                properties = statHandler.app.addCommonStatParams(properties);
            }
            const asAnonymous = !!(flags & StatTypes_1.StatFlags.Anonymous);
            const userIds = getUserIdsPayload(statHandler, asAnonymous);
            result = {
                apiKey: apiKey,
                apiHost: statHandler.apiHost,
                context: getStatContext(statHandler),
                timestamp: timestamp,
                event: event,
                properties: properties,
                anonymousId: userIds.anonymousId,
                userId: userIds.userId,
            };
        }
    }
    return result;
}
function buildBatchFlags(flags) {
    let batchFlags = StatTypes_1.StatFlags.None;
    if (flags & StatTypes_1.StatFlags.OnlyForWorker) {
        batchFlags |= StatTypes_1.StatFlags.OnlyForWorker;
    }
    return batchFlags;
}
function page(statHandler, apiKey, properties, flags = StatTypes_1.StatFlags.None) {
    const asAnonymous = !!(flags & StatTypes_1.StatFlags.Anonymous);
    const userIds = getUserIdsPayload(statHandler, asAnonymous);
    const context = getStatContext(statHandler);
    const { path = '', title = '' } = properties || {};
    if (path) {
        context.page.path = path;
    }
    if (title) {
        context.page.title = title;
    }
    const params = {
        apiKey: apiKey,
        apiHost: statHandler.apiHost,
        context: getStatContext(statHandler),
        timestamp: new Date(),
        anonymousId: userIds.anonymousId,
        userId: userIds.userId,
        properties: {
            utm_campaign: properties?.utm_campaign,
            utm_content: properties?.utm_content,
            utm_custom: properties?.utm_custom,
            utm_medium: properties?.utm_medium,
            utm_source: properties?.utm_source,
            utm_term: properties?.utm_term,
        }
    };
    sendEvent(statHandler, StatTypes_1.StatFlags.None, {
        event: 'page',
        payload: params,
    });
}
exports.page = page;
function track(statHandler, apiKey, event, inputProperties, flags = StatTypes_1.StatFlags.None, timestamp = new Date()) {
    const trackParams = buildTrackParams(statHandler, apiKey, event, inputProperties, flags, timestamp);
    if (trackParams) {
        const batchMode = Boolean(flags & StatTypes_1.StatFlags.SendInBatch) || statHandler.batches?.alwaysBatch;
        const immediateMode = Boolean(flags & StatTypes_1.StatFlags.SendImmediately);
        const shouldBatch = batchMode && !immediateMode;
        if (shouldBatch && !statHandler.batches) {
            console.warn('Event wants batch mode but .batches config is missing. Falling back to immediate mode.');
        }
        if (shouldBatch && statHandler.batches) {
            let apiKeyBucket = statHandler.batches.buckets.get(apiKey);
            if (!apiKeyBucket) {
                apiKeyBucket = new Map();
                statHandler.batches.buckets.set(apiKey, apiKeyBucket);
            }
            const batchFlags = buildBatchFlags(flags);
            let batch = apiKeyBucket.get(batchFlags);
            if (!batch) {
                batch = {
                    tracks: [],
                    flags: batchFlags,
                    flushAt: statHandler.batches.flushAt,
                };
                apiKeyBucket.set(batchFlags, batch);
            }
            if (batch.tracks.length >= batch.flushAt) {
                flushBatchInternal(statHandler, batch, apiKey);
            }
            batch.tracks.push(trackParams);
        }
        else {
            sendEvent(statHandler, flags, {
                event: 'track',
                payload: trackParams,
            });
        }
    }
}
exports.track = track;
function trackBatch(statHandler, apiKey, events, flags = StatTypes_1.StatFlags.None) {
    const tracks = [];
    events.forEach(event => {
        const params = buildTrackParams(statHandler, apiKey, event.event, event.properties, flags, event.timestamp || new Date());
        if (params) {
            tracks.push(params);
        }
    });
    const tempBatch = {
        tracks: tracks,
        flags: buildBatchFlags(flags),
        flushAt: tracks.length,
    };
    flushBatchInternal(statHandler, tempBatch, apiKey);
}
exports.trackBatch = trackBatch;
function clearBatches(statHandler) {
    if (statHandler.batches) {
        statHandler.batches.buckets.clear();
    }
}
exports.clearBatches = clearBatches;
function flushBatchWithBeacon(statHandler, batch, apiKey) {
    if (batch.tracks.length) {
        const params = {
            apiKey: apiKey,
            apiHost: statHandler.apiHost,
            tracks: [...batch.tracks],
        };
        StatsApi.trackBatchWithBeacon(params);
        batch.tracks.length = 0;
    }
}
function flushBatches(statHandler, isPageHidden = false) {
    const handler = isPageHidden ? flushBatchWithBeacon : flushBatchInternal;
    if (statHandler.batches) {
        statHandler.batches.buckets.forEach((bucket, apiKey) => {
            bucket.forEach(batch => {
                handler(statHandler, batch, apiKey);
            });
        });
    }
}
exports.flushBatches = flushBatches;
function flushBatch(statHandler, apiKey) {
    if (statHandler.batches) {
        const bucket = statHandler.batches.buckets.get(apiKey);
        if (bucket) {
            bucket.forEach(batch => {
                flushBatchInternal(statHandler, batch, apiKey);
            });
        }
    }
}
exports.flushBatch = flushBatch;
function flushBatchInternal(statHandler, batch, apiKey) {
    if (batch.tracks.length) {
        const params = {
            apiKey: apiKey,
            apiHost: statHandler.apiHost,
            tracks: batch.tracks,
        };
        sendEvent(statHandler, batch.flags, {
            event: 'trackBatch',
            payload: params,
        });
        batch.tracks.length = 0;
    }
}
function getUserIdsPayload(statHandler, asAnonymous) {
    return {
        anonymousId: asAnonymous ? getFullyAnonymousId() : statHandler.anonymousId,
        userId: asAnonymous ? undefined : statHandler.userId,
    };
}
function sendEvent(statHandler, flags, params) {
    if (statHandler.worker) {
        statHandler.worker.postMessage(params);
    }
    else {
        const onlyWorker = !!(flags & StatTypes_1.StatFlags.OnlyForWorker);
        if (onlyWorker) {
        }
        else {
            switch (params.event) {
                case 'identify':
                    {
                        StatsApi.identify(params.payload);
                    }
                    break;
                case 'track':
                    {
                        StatsApi.track(params.payload);
                    }
                    break;
                case 'trackBatch':
                    {
                        StatsApi.trackBatch(params.payload);
                    }
                    break;
                case 'page':
                    {
                        StatsApi.page(params.payload);
                    }
                    break;
            }
        }
    }
}
function getStatContext(statHandler) {
    const context = {
        library: {
            name: statHandler.library,
            version: statHandler.version,
        },
        page: statHandler.app.getStatContextPage(statHandler),
        userAgent: statHandler.app.getUserAgent(),
        sessionId: statHandler.app.getSessionId(),
    };
    if (statHandler.app.name) {
        const { name, version = '0.0.1' } = statHandler.app;
        context.app = { name, version };
    }
    return context;
}
function updateUserForHandler(statHandler, anonymousId, userId, source) {
    if (!anonymousId || userId === 'null' || userId === 'undefined') {
        statHandler.app.sendError('Stats invalid userId', {
            anonymousId: anonymousId,
            userId: userId,
            source: source,
        });
    }
    else {
        statHandler.anonymousId = anonymousId;
        statHandler.userId = userId;
        statHandler.__lastUserSource = source;
    }
}
exports.updateUserForHandler = updateUserForHandler;
function createStatHandler(options) {
    const statHandler = {
        __statId: NEXT_STAT_HANDLER_ID++,
        __lastUserSource: undefined,
        apiHost: options.apiHost,
        library: options.library,
        version: options.version,
        anonymousId: '',
        userId: undefined,
        app: options.app,
        isFirstSession: false,
        onceInSessionSentEvents: new Set(),
        worker: undefined,
        batches: undefined,
        flushBatchesIntervalId: undefined,
    };
    if (options.batches) {
        statHandler.batches = {
            buckets: new Map(),
            flushAt: options.batches.flushAt,
        };
    }
    const userData = statHandler.app.getStoredUserIds();
    if (!userData.anonymousId) {
        userData.anonymousId = createAnonymousId();
        statHandler.app.storeUserIds(userData);
    }
    updateUserForHandler(statHandler, userData.anonymousId, userData.userId, 'initialization');
    return statHandler;
}
exports.createStatHandler = createStatHandler;
//# sourceMappingURL=Stat.js.map